// src/utils/pricingTiers.js
//
// Time-based registration pricing tiers (Early Bird → Regular → Late
// → ...). Coaches create the tier list on a season; the parent
// registration page + Cloud Function call into this resolver to find
// the active tier at the moment of checkout, with the price the
// parent actually pays.
//
// Schema (lives on season doc as `pricingTiers`):
//   [
//     {
//       id:                'early-bird',   // stable id
//       name:              'Early Bird',
//       priceInCents:      265000,
//       startsAt:          Timestamp,      // tier window opens
//       endsAt:            Timestamp,      // tier window closes
//       maxRegistrations:  20 | null,      // optional cap
//       sortOrder:         0,              // for stable ordering
//     },
//     ...
//   ]
//
// Selection logic (this file's getActivePricingTier):
//   1. Find all tiers where now is within [startsAt, endsAt] AND
//      capacity isn't exhausted.
//   2. Pick the one with the lowest sortOrder; ties broken by
//      earliest startsAt.
//   3. If no active tier: return null. The caller decides what to
//      do (registration page shows "registration closed" empty state).
//
// Why these rules:
//   - Overlapping tiers are intentionally allowed. A coach who wants
//     to give an "Early Bird Extension" tier that overlaps with
//     Regular (lower sortOrder = wins) can do it without restructuring
//     existing tiers.
//   - Dates are stored as Timestamps; we coerce in toDate() to handle
//     either Firestore Timestamps or plain JS Dates / ISO strings.

import { toSeasonDate, endOfSeasonDay } from './seasonDates';

export const DEFAULT_TIER_NAMES = ['Early Bird', 'Regular', 'Late', 'Final'];

// Tier windows are date-only (coach picks calendar days) — parse via the
// shared season-date convention: local midnight, UTC-midnight legacy repair.
const toDate = toSeasonDate;

/**
 * Validate a tier list. Returns { ok: boolean, errors: [] }.
 * - Each tier must have id, name, priceInCents >= 0, startsAt < endsAt.
 * - Names should be non-empty.
 * - At least one tier required.
 */
export function validatePricingTiers(tiers) {
  const errors = [];
  if (!Array.isArray(tiers) || tiers.length === 0) {
    errors.push('At least one pricing tier is required.');
    return { ok: false, errors };
  }
  tiers.forEach((t, i) => {
    const label = t.name || `Tier ${i + 1}`;
    if (!t.id) errors.push(`${label}: missing id.`);
    if (!t.name || !t.name.trim()) errors.push(`${label}: name required.`);
    if (typeof t.priceInCents !== 'number' || t.priceInCents < 0) {
      errors.push(`${label}: price must be 0 or greater.`);
    }
    const starts = toDate(t.startsAt);
    const ends = toDate(t.endsAt);
    if (!starts) errors.push(`${label}: start date required.`);
    if (!ends) errors.push(`${label}: end date required.`);
    if (starts && ends && starts >= ends) {
      errors.push(`${label}: end date must be after start date.`);
    }
    if (t.maxRegistrations != null) {
      const n = Number(t.maxRegistrations);
      if (!Number.isFinite(n) || n < 1) {
        errors.push(`${label}: cap must be 1 or greater (or empty for unlimited).`);
      }
    }
  });
  return { ok: errors.length === 0, errors };
}

/**
 * Resolve the currently-active pricing tier given the season's tier
 * list and (optionally) the current registration counts per tier.
 *
 * @param {Array} tiers — pricingTiers array from the season doc
 * @param {Object} opts
 * @param {Date}   opts.now — defaults to new Date()
 * @param {Object} opts.tierFillCounts — optional { [tierId]: count }
 *                  used to enforce maxRegistrations caps
 * @returns {Object|null} the active tier, or null if none active
 */
export function getActivePricingTier(tiers, { now = new Date(), tierFillCounts = {} } = {}) {
  if (!Array.isArray(tiers) || tiers.length === 0) return null;
  const candidates = tiers.filter((t) => {
    const starts = toDate(t.startsAt);
    const ends = endOfSeasonDay(t.endsAt); // end date is inclusive of its full day
    if (!starts || !ends) return false;
    if (now < starts || now > ends) return false;
    if (t.maxRegistrations != null) {
      const filled = tierFillCounts[t.id] || 0;
      if (filled >= t.maxRegistrations) return false;
    }
    return true;
  });
  if (candidates.length === 0) return null;
  // Lowest sortOrder wins (earlier-defined tier is more specific);
  // ties broken by earliest startsAt.
  candidates.sort((a, b) => {
    const so = (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
    if (so !== 0) return so;
    const aStart = toDate(a.startsAt)?.getTime() ?? 0;
    const bStart = toDate(b.startsAt)?.getTime() ?? 0;
    return aStart - bStart;
  });
  return candidates[0];
}

/**
 * Compute proration-suggested tier prices for the calculator helper.
 * Given a full season price and start/end dates, returns suggested
 * prices for [Early Bird, Regular, Mid-season, Late, Final] based
 * on monthly proration. The coach can edit any of them after.
 *
 * @param {Object} opts
 * @param {number} opts.fullPriceInCents — what someone pays if they
 *                  register at season start
 * @param {Date}   opts.seasonStart
 * @param {Date}   opts.seasonEnd
 * @param {number} opts.tierCount — 2 to 5
 * @returns {Array} suggested tier objects [{ name, priceInCents, startsAt, endsAt }]
 */
export function suggestProratedTiers({ fullPriceInCents, seasonStart, seasonEnd, tierCount = 3 }) {
  if (!fullPriceInCents || !seasonStart || !seasonEnd) return [];
  const start = toDate(seasonStart);
  const end = toDate(seasonEnd);
  if (!start || !end || end <= start) return [];

  const totalMs = end.getTime() - start.getTime();
  const segmentMs = totalMs / tierCount;

  const out = [];
  for (let i = 0; i < tierCount; i++) {
    const tierStart = new Date(start.getTime() + i * segmentMs);
    const tierEnd = new Date(start.getTime() + (i + 1) * segmentMs - 1);
    // Each later tier prorates: i=0 → full price, i=1 → (n-1)/n, etc.
    // For a 4-tier 10-month $2800 season, that gives roughly:
    //   Tier 1 (months 0-2.5): $2800
    //   Tier 2 (months 2.5-5): $2100
    //   Tier 3 (months 5-7.5): $1400
    //   Tier 4 (months 7.5-10): $700
    // Rounded to nearest dollar.
    const remaining = (tierCount - i) / tierCount;
    const priceInCents = Math.round(fullPriceInCents * remaining / 100) * 100;
    out.push({
      name: DEFAULT_TIER_NAMES[i] || `Tier ${i + 1}`,
      priceInCents,
      startsAt: tierStart,
      endsAt: tierEnd,
    });
  }
  return out;
}

/** Format cents as USD without trailing zeros. */
export function formatTierPrice(cents) {
  if (cents == null) return '—';
  if (cents === 0) return 'Free';
  const dollars = cents / 100;
  return `$${dollars.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}
